import { useEffect, useState, useCallback, useRef } from 'react'
import webSocketService, { WebSocketServiceCallbacks } from '@/services/websocketService'

interface WebSocketMessage {
  type: string
  file_upload_id?: number
  chat_session_id?: number
  message_id?: number
  status?: string
  requires_review?: boolean
  message?: string
  classification?: {
    document_type: string
    transaction_type: string
    confidence: number
    primary_account: {
      account_code: string
      account_name: string
      reasoning: string
    }
    requires_review: boolean
    review_reason?: string
    error?: string
  }
  extracted_data?: {
    vendor_or_customer: string
    total_amount: number
    document_number: string
    document_date: string
    line_items_count: number
  }
  timestamp?: string
}

interface UseWebSocketOptions {
  chatSessionId?: number
  onFileProcessingComplete?: (data: WebSocketMessage) => void
  onError?: (error: Error) => void
  autoConnect?: boolean
}

interface UseWebSocketReturn {
  connectionStatus: 'connected' | 'disconnected' | 'connecting' | 'error'
  connect: () => void
  disconnect: () => void
  ping: () => void
  lastMessage: WebSocketMessage | null
  error: Error | null
}

export function useWebSocket({
  chatSessionId,
  onFileProcessingComplete,
  onError,
  autoConnect = true
}: UseWebSocketOptions = {}): UseWebSocketReturn {
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting' | 'error'>('disconnected')
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const subscriptionRef = useRef<any>(null)
  const isConnectingRef = useRef(false)

  // Callback handlers
  const handleConnected = useCallback(() => {
    console.log('WebSocket connected')
    setConnectionStatus('connected')
    setError(null)
    isConnectingRef.current = false
  }, [])

  const handleDisconnected = useCallback(() => {
    console.log('WebSocket disconnected')
    setConnectionStatus('disconnected')
    isConnectingRef.current = false
  }, [])

  const handleError = useCallback((err: Error) => {
    console.error('WebSocket error:', err)
    setError(err)
    setConnectionStatus('error')
    isConnectingRef.current = false
    onError?.(err)
  }, [onError])

  const handleFileProcessingComplete = useCallback((data: WebSocketMessage) => {
    console.log('File processing complete:', data)
    setLastMessage(data)
    onFileProcessingComplete?.(data)
  }, [onFileProcessingComplete])

  const handleSubscribed = useCallback(() => {
    console.log('Successfully subscribed to chat session')
    setConnectionStatus('connected')
  }, [])

  // Connect function
  const connect = useCallback(() => {
    if (isConnectingRef.current || connectionStatus === 'connected') {
      return
    }

    if (!chatSessionId) {
      console.warn('Cannot connect WebSocket: No chat session ID provided')
      return
    }

    try {
      isConnectingRef.current = true
      setConnectionStatus('connecting')
      setError(null)

      const callbacks: WebSocketServiceCallbacks = {
        onConnected: handleConnected,
        onDisconnected: handleDisconnected,
        onError: handleError,
        onFileProcessingComplete: handleFileProcessingComplete,
        onSubscribed: handleSubscribed
      }

      // Connect to WebSocket service
      webSocketService.connect(callbacks)

      // Subscribe to chat session
      subscriptionRef.current = webSocketService.subscribeToChatSession(chatSessionId)

      if (!subscriptionRef.current) {
        throw new Error('Failed to subscribe to chat session')
      }

    } catch (err) {
      console.error('Failed to connect WebSocket:', err)
      handleError(err as Error)
    }
  }, [chatSessionId, handleConnected, handleDisconnected, handleError, handleFileProcessingComplete, handleSubscribed])

  // Disconnect function
  const disconnect = useCallback(() => {
    console.log('Disconnecting WebSocket...')
    webSocketService.disconnect()
    subscriptionRef.current = null
    setConnectionStatus('disconnected')
    isConnectingRef.current = false
  }, [])

  // Ping function
  const ping = useCallback(() => {
    webSocketService.ping()
  }, [])

  // Auto-connect on mount if enabled and chatSessionId is provided
  useEffect(() => {
    if (autoConnect && chatSessionId && connectionStatus === 'disconnected' && !isConnectingRef.current) {
      connect()
    }
  }, [autoConnect, chatSessionId, connectionStatus, connect])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        disconnect()
      }
    }
  }, [])

  // Handle chatSessionId changes
  useEffect(() => {
    if (chatSessionId && subscriptionRef.current) {
      // Disconnect and reconnect when session changes
      disconnect()
      const timeoutId = setTimeout(() => {
        if (autoConnect) {
          connect()
        }
      }, 100)
      
      return () => clearTimeout(timeoutId)
    }
  }, [chatSessionId, autoConnect, connect, disconnect])

  return {
    connectionStatus,
    connect,
    disconnect,
    ping,
    lastMessage,
    error
  }
}

export default useWebSocket